import React from "react";
import classNames from "classnames";
import { Form } from "react-bootstrap";
import Select from "react-select";

/**
 * One step of a Part's route.
 *
 * The step's position in the list is its sequence_no — there is no field to edit,
 * it is derived from the order and renumbered whenever steps move.
 *
 * An operation is atomic: it runs on one machine. Machines are given as a single
 * ordered list — the first is the one the step normally runs on and the one the Job
 * Schedule pre-fills, the rest are alternates. Empty means any capable machine.
 */
const PartOperationCard = ({
    id,
    position,
    row,
    operationOptions,
    operationsById,
    devicesForOperation = [],
    deviceLabel = (deviceId) => deviceId,
    loadingDevices = false,
    onChange,
    onRemove,
    canRemove = true,
    dragHandleProps,
}) => {
    const selectedOption =
        operationOptions.find((o) => o.value === row.operation_id) || null;
    const operation = operationsById[row.operation_id];

    // One ordered list: first is the machine the step normally runs on, the rest are
    // alternates. Empty means any capable machine.
    const chosen = row.device_ids || [];
    const remainingOptions = devicesForOperation
        .filter((d) => !chosen.includes(d.id))
        .map((d) => ({ value: d.id, label: deviceLabel(d.id) }));

    const moveMachine = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= chosen.length) return;
        const next = [...chosen];
        [next[index], next[target]] = [next[target], next[index]];
        onChange?.(id, { device_ids: next });
    };

    const removeMachine = (deviceId) =>
        onChange?.(id, { device_ids: chosen.filter((d) => d !== deviceId) });

    const addMachine = (option) => {
        if (!option) return;
        onChange?.(id, { device_ids: [...chosen, option.value] });
    };

    // Duration is held in seconds; the inputs split it into minutes + seconds.
    const totalSeconds = Number(row.standard_duration) || 0;
    const minutes = totalSeconds ? Math.floor(totalSeconds / 60) : "";
    const seconds = totalSeconds ? totalSeconds % 60 : "";

    const setDuration = (nextMinutes, nextSeconds) => {
        const m = parseInt(nextMinutes || 0, 10);
        const s = parseInt(nextSeconds || 0, 10);
        const total = m * 60 + s;
        onChange?.(id, {
            standard_duration: Number.isNaN(total) || total === 0 ? "" : total,
        });
    };

    const durationLabel = totalSeconds
        ? `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`
        : null;

    return (
        <div className="collapsible-container border rounded mb-2">
            <div className="collapsible-header d-flex justify-content-between align-items-center bg-color-e6e6e6 p-2">
                <div className="d-flex align-items-center">
                    <div
                        {...dragHandleProps}
                        className="d-flex align-items-center me-2"
                        style={{ cursor: "grab" }}
                        title="Drag to reorder"
                    >
                        <span
                            className="badge bg-secondary me-2"
                            style={{ minWidth: "28px" }}
                        >
                            {position}
                        </span>
                        <i className="fa fa-grip-vertical text-muted" />
                    </div>
                    <span
                        className={classNames("rounded-circle px-9px me-2", {
                            "bg-success": operation?.is_productive === true,
                            "bg-danger": operation?.is_productive === false,
                            "bg-color-a9b0bc": !operation,
                        })}
                    />
                    <span>
                        {operation?.name || "Select operation"}
                        {operation && (
                            <span className="text-muted">
                                {" | "}
                                {durationLabel || "no duration"}
                                {" | "}
                                {chosen.length === 0
                                    ? "any capable machine"
                                    : chosen.length === 1
                                      ? deviceLabel(chosen[0])
                                      : `${deviceLabel(chosen[0])} +${chosen.length - 1}`}
                            </span>
                        )}
                    </span>
                </div>
                <div className="collapsible-controls">
                    {canRemove && (
                        <i
                            className="fa fa-trash-alt me-2 tw-cursor-pointer"
                            title="Remove step"
                            onClick={() => onRemove?.(id)}
                        />
                    )}
                </div>
            </div>

            <div className="p-3">
                <div className="row">
                    <div className="col-md-6">
                        <Form.Group
                            className="mb-3"
                            controlId={`part_operation_${id}`}
                        >
                            <Form.Label className="font-weight-normal">
                                Operation <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                                inputId={`part_operation_${id}`}
                                options={operationOptions}
                                placeholder="Select"
                                value={selectedOption}
                                onChange={(selected) =>
                                    onChange?.(id, {
                                        operation_id: selected
                                            ? selected.value
                                            : null,
                                        // A different operation has a different capable
                                        // pool, so earlier machine picks no longer apply.
                                        device_ids: [],
                                    })
                                }
                            />
                            {!row.operation_id && (
                                <div className="text-danger small">
                                    Operation is required
                                </div>
                            )}
                        </Form.Group>
                    </div>

                    <div className="col-md-6">
                        <Form.Group
                            className="mb-3"
                            controlId={`part_operation_duration_${id}`}
                        >
                            <Form.Label className="font-weight-normal">
                                Duration
                            </Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="number"
                                    placeholder="Minutes"
                                    min={0}
                                    value={minutes}
                                    onChange={(e) =>
                                        setDuration(e.target.value, seconds)
                                    }
                                />
                                <Form.Control
                                    type="number"
                                    placeholder="Seconds"
                                    min={0}
                                    max={59}
                                    value={seconds}
                                    onChange={(e) =>
                                        setDuration(minutes, e.target.value)
                                    }
                                />
                            </div>
                            <Form.Text muted>
                                How long this step takes for this part. The Job
                                Schedule starts from this and can override it.
                            </Form.Text>
                        </Form.Group>
                    </div>
                </div>

                <Form.Group controlId={`part_operation_machines_${id}`}>
                    <Form.Label className="font-weight-normal">
                        Machines
                    </Form.Label>

                    {!row.operation_id ? (
                        <div className="text-muted small">
                            Select an operation first.
                        </div>
                    ) : loadingDevices ? (
                        <div className="text-muted small">
                            Loading machines...
                        </div>
                    ) : devicesForOperation.length === 0 ? (
                        <div className="text-danger small">
                            No machine can perform this operation yet. Link one
                            under Devices &rarr; Capability.
                        </div>
                    ) : (
                        <>
                            {chosen.length > 0 && (
                                <div className="d-flex flex-column gap-2 mb-2">
                                    {chosen.map((deviceId, index) => (
                                        <div
                                            key={deviceId}
                                            className="d-flex align-items-center justify-content-between border rounded px-2 py-1"
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <span
                                                    className={`badge ${index === 0 ? "bg-primary" : "bg-light text-dark"}`}
                                                >
                                                    {index === 0
                                                        ? "Default"
                                                        : `Alt ${index}`}
                                                </span>
                                                <span className="text-truncate">
                                                    {deviceLabel(deviceId)}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link p-0 px-1 text-secondary"
                                                    title="Move up"
                                                    disabled={index === 0}
                                                    onClick={() =>
                                                        moveMachine(index, -1)
                                                    }
                                                >
                                                    <i className="fa fa-chevron-up" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link p-0 px-1 text-secondary"
                                                    title="Move down"
                                                    disabled={
                                                        index ===
                                                        chosen.length - 1
                                                    }
                                                    onClick={() =>
                                                        moveMachine(index, 1)
                                                    }
                                                >
                                                    <i className="fa fa-chevron-down" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link p-0 px-1 text-danger"
                                                    title="Remove"
                                                    onClick={() =>
                                                        removeMachine(deviceId)
                                                    }
                                                >
                                                    <i className="fa fa-times" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Select
                                inputId={`part_operation_machines_${id}`}
                                options={remainingOptions}
                                value={null}
                                isDisabled={remainingOptions.length === 0}
                                placeholder={
                                    remainingOptions.length === 0
                                        ? "Every capable machine added"
                                        : chosen.length === 0
                                          ? "Any capable machine — add one to restrict"
                                          : "Add an alternate"
                                }
                                onChange={addMachine}
                            />
                        </>
                    )}

                    <Form.Text muted>
                        Leave empty to allow any machine that can perform this
                        operation. Add machines to restrict this part to them —
                        the first is the one it normally runs on and the one the
                        Job Schedule pre-fills.
                    </Form.Text>
                </Form.Group>
            </div>
        </div>
    );
};

export default PartOperationCard;
